"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import { PageFuturesSchema } from "@/sanity/pages/PageFuturesTemplate";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import ChipSerif from "@/components/global/ui/ChipSerif";
import CardAnimationNew from "@/components/global/CardAnimation";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/shadcn/ui/carousel";
import { GROQImage } from "@/sanity/lib/definitions";

interface MobileTraderCardProps {
  item: {
    photo?: GROQImage;
    image?: GROQImage;
    name?: string;
    position?: string;
    tradersText?: PortableTextBlock[];
    tradersQuote?: PortableTextBlock[];
  };
  futures?: boolean;
}

function MobileTraderCard({ item, futures = false }: MobileTraderCardProps) {
  return (
    <div
      className={cn(
        "flex relative flex-col items-center w-full rounded-[2rem] overflow-hidden",
        futures ? "bg-[#DDDFEC]" : "bg-[#DEE4DB]",
      )}
    >
      <div className="overflow-hidden relative w-full max-w-[48rem]">
        {item.photo && (
          <Image
            className="object-cover w-full"
            src={item.photo}
            alt="Photo"
          />
        )}
        <div
          className={cn(
            "absolute left-1/2 -translate-x-1/2 -bottom-px w-full h-[25rem] bg-gradient-to-t from-[10%]",
            futures
              ? "from-[#DDDFEC]"
              : "from-[#DEE4DB]",
          )}
        />
      </div>

      <div className="flex relative flex-col items-start w-full pb-[3rem] px-[3rem] sm:pb-[4rem] sm:px-[4rem]">
        <div className="flex items-start gap-[1.75rem]">
          {item.image && (
            <Image className="!w-[4.375rem]" src={item.image} alt="" />
          )}

          <div className="flex flex-col items-start">
            {item.name && (
              <p className="font-medium tracking-tight leading-none mb-[.5rem] text-[2.75rem] text-[#07171B]">
                {item.name}
              </p>
            )}

            {item.position && (
              <p className="mb-0 font-medium tracking-tight text-[2rem] text-[#07171B]/50 leading-[1.2]">
                {item.position}
              </p>
            )}
          </div>
        </div>

        <hr className="w-full border-t my-[2.25rem] [border-image:repeating-linear-gradient(to_right,#07171B_0,#07171B_7px,transparent_7px,transparent_14px)_1]" />

        {item.tradersText && (
          <div className="richtext richtext-start prose-p:text-[#07171B]/80">
            <PortableText
              value={item.tradersText as PortableTextBlock[]}
              components={richTextComponent}
            />
          </div>
        )}

        <hr className="w-full border-t my-[2.25rem] [border-image:repeating-linear-gradient(to_right,#07171B_0,#07171B_7px,transparent_7px,transparent_14px)_1]" />

        {item.tradersQuote && (
          <div className="richtext richtext-start prose-p:font-medium prose-p:text-[2rem] prose-p:text-[#07171B]/70">
            <PortableText
              value={item.tradersQuote as PortableTextBlock[]}
              components={richTextComponent}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default function TradersSection({
  data,
  futures = false,
  className,
}: {
  data: PageHomeSchema | PageFuturesSchema;
  futures?: boolean;
  className?: string;
}) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;

    const onSelect = () => {
      setCurrent(carouselApi.selectedScrollSnap());
      setCount(carouselApi.scrollSnapList().length);
    };

    onSelect();
    carouselApi.on("select", onSelect);
    carouselApi.on("reInit", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
      carouselApi.off("reInit", onSelect);
    };
  }, [carouselApi]);

  return (
    <section
      className={cn(
        "relative w-full rounded-[1.25rem] py-[7rem] lg:pt-[16.25rem] lg:pb-[18.25rem] overflow-clip",
        className,
      )}
    >
      <div className="flex flex-col items-start w-[min(229.25rem,100%)] mx-auto px-[2rem] lg:px-0 relative z-[5]">
        <div className="hidden lg:block container w-full relative z-10 !max-w-[190rem] pb-[10rem]">
          <div className="sticky !top-[10rem]" data-cards-pin="true">
            <div className="flex flex-col items-center" data-cards-title="true">
              <ChipSerif className="text-white/70" text={data?.tradersChip} />

              {data?.tradersTitle && (
                <div className="richtext mb-[6.75rem] prose-p:text-white/70 prose-p:w-[min(50rem,100%)] prose-headings:text-white prose-headings:w-[min(45rem,100%)] lg:prose-headings:w-[min(70.5rem,100%)]">
                  <PortableText
                    value={data.tradersTitle}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>

            {data?.traderPerson && (
              <div className="w-full pt-[6rem]">
                <CardAnimationNew
                  cards={data?.traderPerson}
                  futures={futures}
                />
              </div>
            )}
          </div>
        </div>

        <div className="lg:hidden w-full">
          <div className="flex flex-col items-center">
            <ChipSerif className="text-white/70" text={data?.tradersChip} />

            {data?.tradersTitle && (
              <div className="richtext mb-[6.75rem] prose-p:text-white/70 prose-p:w-[min(50rem,100%)] prose-headings:text-white prose-headings:w-[min(45rem,100%)]">
                <PortableText
                  value={data.tradersTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {data?.traderPerson && data.traderPerson.length > 0 && (
            <div className="w-full">
              {/* Arrows + Progress Bar */}
              <div className="flex justify-center items-center gap-[2rem] mb-[4rem] px-[2rem]">
                <button
                  type="button"
                  onClick={() => carouselApi?.scrollPrev()}
                  className="flex flex-shrink-0 justify-center items-center text-white rounded-full border transition-all w-[5rem] h-[5rem] border-white/20 hover:bg-white/10"
                  aria-label="Previous slide"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="flex-shrink-0 h-auto rotate-180 w-[2.5rem]"
                  >
                    <path
                      d="M13.5 4.5L12.4275 5.54475L18.1125 11.25H3V12.75H18.1125L12.4275 18.4298L13.5 19.5L21 12L13.5 4.5Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>

                <div className="overflow-hidden w-full rounded-full h-[0.5rem] bg-white/20 max-w-[34rem]">
                  <div
                    className="h-full rounded-full transition-all duration-300 ease-out bg-white/70"
                    style={{
                      width: count
                        ? `${((current + 1) / count) * 100}%`
                        : "0%",
                    }}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => carouselApi?.scrollNext()}
                  className="flex flex-shrink-0 justify-center items-center text-white rounded-full border transition-all w-[5rem] h-[5rem] border-white/20 hover:bg-white/10"
                  aria-label="Next slide"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="flex-shrink-0 h-auto w-[2.5rem]"
                  >
                    <path
                      d="M13.5 4.5L12.4275 5.54475L18.1125 11.25H3V12.75H18.1125L12.4275 18.4298L13.5 19.5L21 12L13.5 4.5Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>

              <Carousel
                opts={{ loop: true, align: "center" }}
                setApi={setCarouselApi}
                className="w-full"
              >
                <CarouselContent className="-ml-[2rem]">
                  {data.traderPerson.map((item, idx) => (
                    <CarouselItem
                      key={item.name ?? `trader-${idx}`}
                      className="pl-[2rem] basis-[92%]"
                    >
                      <MobileTraderCard item={item} futures={futures} />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
